import React, { useEffect, useState } from 'react';
import Layout from '../../components/Layout';
import { studentService } from '../../services/api';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';

const ParentFees: React.FC = () => {
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const [fees, setFees] = useState<any[]>([]);
  const [students, setStudents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState<string>('');

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    if (selectedStudent) {
      fetchFees();
    }
  }, [selectedStudent]);

  const fetchStudents = async () => {
    try {
      // Fetch all students to find those linked to this parent
      const response = await studentService.getAll();
      const parentStudents = response.data.students || [];
      setStudents(parentStudents);
      if (parentStudents.length > 0) {
        setSelectedStudent(parentStudents[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch students:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchFees = async () => {
    try {
      const response = await studentService.getAll();
      const studentFees = response.data.students?.find((s: any) => s.id === selectedStudent)?.fees || [];
      setFees(studentFees);
    } catch (error) {
      console.error('Failed to fetch fees:', error);
    }
  };

  if (loading) {
    return <Layout title="My Children's Fees"><div className="text-center py-8">Loading...</div></Layout>;
  }

  const currentStudent = students.find(s => s.id === selectedStudent);
  const totalPending = fees.reduce((sum, f) => sum + (f.pendingAmount || 0), 0);
  const totalPaid = fees.reduce((sum, f) => sum + (f.paidAmount || 0), 0);

  return (
    <Layout title="My Children's Fees">
      <div className="mb-6">
        <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Select Child</label>
        <select
          value={selectedStudent}
          onChange={(e) => setSelectedStudent(e.target.value)}
          className={`w-full md:w-64 border rounded px-3 py-2 ${isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300'}`}
        >
          {students.map((student) => (
            <option key={student.id} value={student.id}>
              {student.user?.firstName} {student.user?.lastName} (Class {student.class?.name})
            </option>
          ))}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Paid</p>
          <p className="text-2xl font-bold text-green-600">₹{totalPaid.toFixed(2)}</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Pending Amount</p>
          <p className="text-2xl font-bold text-red-600">₹{totalPending.toFixed(2)}</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow p-4`}>
          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Total Records</p>
          <p className="text-2xl font-bold text-blue-600">{fees.length}</p>
        </div>
      </div>

      {/* Fees Table */}
      <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
        <table className="w-full">
          <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
            <tr>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Fee Type</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Total Amount</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Paid</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Pending</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Due Date</th>
              <th className={`text-left px-6 py-3 text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
            </tr>
          </thead>
          <tbody>
            {fees.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center py-8 text-gray-500">No fee records found</td>
              </tr>
            ) : (
              fees.map((fee, index) => (
                <tr key={index} className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{fee.type || 'General'}</td>
                  <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>₹{fee.totalAmount || 0}</td>
                  <td className={`px-6 py-4 text-green-600`}>₹{fee.paidAmount || 0}</td>
                  <td className={`px-6 py-4 text-red-600`}>₹{fee.pendingAmount || 0}</td>
                  <td className={`px-6 py-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{fee.dueDate ? new Date(fee.dueDate).toLocaleDateString() : 'N/A'}</td>
                  <td className={`px-6 py-4`}>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      fee.status === 'paid' ? 'bg-green-100 text-green-800' :
                      fee.status === 'overdue' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {fee.status || 'pending'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
};

export default ParentFees;
